import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export interface ProformaPayment {
  id: number;
  paymentDate: string;
  amount: number;
  narrative: string;
  account: string | null;
  salesOrder: string | null;
  documentType: "quote" | "order" | null;
  status: "pending" | "action_running" | "external_done" | "posted" | "action_unknown" | "failed" | "posting_failed" | string;
  notes: string | null;
  detectedAt: string;
  confirmedAt: string | null;
  externalActionCompletedAt: string | null;
  merlinPostedAt: string | null;
  failureReason: string | null;
  returnedSalesOrder: string | null;
  canConfirm?: boolean;
  alreadyPosted?: boolean;
  canClearAlreadyPosted?: boolean;
  retryableInvalidXml?: boolean;
  canPayInvoice?: boolean;
  resumable?: boolean;
  canInvestigate?: boolean;
  investigatedAt?: string | null;
  investigation?: {
    stage: "quote_conversion" | "sales_email" | "cash_receipt";
    checkedAt: string;
    evidence: Array<string | {
      text: string;
      emailLogId?: number;
    }>;
    auditLogId?: number;
    resolution: "resume_external_action" | "resume_cash_receipt" | "mark_posted" | "remain_locked";
    nextStatus: string;
  } | null;
}

export function useInvestigateProformaPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/cash-receipts/proforma-payments/${id}/investigate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        credentials: "include",
        signal: AbortSignal.timeout(30_000),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "Failed to investigate payment");
      return body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["proforma-payments"] }),
  });
}

export interface ProformaPaymentsResponse {
  payments: ProformaPayment[];
}

export function useProformaPayments() {
  return useQuery<ProformaPaymentsResponse>({
    queryKey: ["proforma-payments"],
    queryFn: async () => {
      let res: Response;
      try {
        res = await fetch(`${BASE}/api/cash-receipts/proforma-payments`, {
          credentials: "include",
          signal: AbortSignal.timeout(20_000),
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === "TimeoutError") {
          throw new Error("Proforma payments took too long to respond. Please try again.");
        }
        throw error;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "Failed to fetch proforma payments");
      if (!Array.isArray(body?.payments)) {
        throw new Error("Invalid response envelope: expected { payments: [...] }");
      }
      return body;
    },
    refetchInterval: (query) => {
      if (!query.state.data?.payments) return false;
      const needsPolling = query.state.data.payments.some(
        (p) => p.status === "action_running" || p.status === "external_done" || p.status === "posting_running"
      );
      return needsPolling ? 5000 : false;
    },
  });
}

export function useConfirmProformaPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      let res: Response;
      try {
        res = await fetch(`${BASE}/api/cash-receipts/proforma-payments/${id}/confirm`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          credentials: "include",
          signal: AbortSignal.timeout(90_000),
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === "TimeoutError") {
          throw new Error("Confirmation is still uncertain after 90 seconds. Refresh the list before taking any further action.");
        }
        throw error;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "Failed to confirm payment");
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proforma-payments"] });
    },
  });
}

export function useClearAlreadyPostedProformaPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/cash-receipts/proforma-payments/${id}/clear-already-posted`, {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "XMLHttpRequest" },
        signal: AbortSignal.timeout(30_000),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "Could not clear already-posted suggestion");
      return body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["proforma-payments"] }),
  });
}

export function usePayProformaInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, invoiceRef }: { id: number; invoiceRef: string }) => {
      let res: Response;
      try {
        res = await fetch(`${BASE}/api/cash-receipts/proforma-payments/${id}/pay-invoice`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "include",
          body: JSON.stringify({ invoiceRef }),
          signal: AbortSignal.timeout(90_000),
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === "TimeoutError") {
          throw new Error("The invoice posting outcome is uncertain. Refresh before taking any further action.");
        }
        throw error;
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "Failed to post payment to invoice");
      return body;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["proforma-payments"] }),
  });
}
